import { Button, FieldMessage, FOCUS_RING, Icon, TextInput, cn, useTranslate } from '@athena/ui'
import { completeOnboardingInput, type OnboardingRole } from '@athena/validation'
import { useEffect, useRef, useState, type ReactNode } from 'react'

export interface OnboardingFlowProps {
  state: {
    role: OnboardingRole | null
    canChangeRole: boolean
    firstName: string
    lastName: string
    birthDate: string | null
  }
  saving: boolean
  completed?: boolean
  error?: string | undefined
  selectRole: (role: OnboardingRole | null) => Promise<void>
  complete: (input: {
    role: OnboardingRole
    firstName: string
    lastName: string
    birthDate?: string
  }) => Promise<void>
}

export function OnboardingFlow({
  state,
  saving,
  completed = false,
  error,
  selectRole,
  complete,
}: OnboardingFlowProps) {
  const t = useTranslate()
  const [firstName, setFirstName] = useState(state.firstName)
  const [lastName, setLastName] = useState(state.lastName)
  const [birthDate, setBirthDate] = useState(state.birthDate ?? '')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const heading = useRef<HTMLHeadingElement>(null)
  const step = state.role ? 2 : 1
  const view = completed ? 'complete' : step
  useEffect(() => {
    heading.current?.focus()
    setErrors({})
  }, [view])

  if (completed) {
    return (
      <section
        className="onboarding-step flex min-h-64 flex-col items-start justify-center gap-5"
        key="complete"
        aria-labelledby="onboarding-heading"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center gap-4">
          <CompletedSteps />
          <p className="text-label text-success-text">{t('onboarding.completeProgress')}</p>
        </div>
        <span className="inline-flex size-12 items-center justify-center rounded-full border border-accent-border bg-accent text-avatar-on-accent">
          <Icon.Check className="size-icon-lg" aria-hidden="true" />
        </span>
        <div className="flex max-w-md flex-col gap-2">
          <h2
            id="onboarding-heading"
            ref={heading}
            tabIndex={-1}
            className="text-h3 font-bold text-text focus:outline-none"
          >
            {t('onboarding.completeTitle')}
          </h2>
          <p className="text-body text-text-secondary text-pretty">
            {t('onboarding.completeBody')}
          </p>
        </div>
      </section>
    )
  }
  return (
    <section
      className="onboarding-step flex flex-col gap-5"
      key={step}
      aria-labelledby="onboarding-heading"
    >
      <div className="flex items-center gap-4">
        <div className="flex items-center" aria-hidden="true">
          <span
            className={cn(
              'inline-flex size-8 items-center justify-center rounded-full border text-caption font-bold',
              step === 1
                ? 'border-primary bg-primary text-text-inverse'
                : 'border-accent-border bg-accent text-avatar-on-accent',
            )}
          >
            1
          </span>
          <span
            className={cn(
              'h-px w-10 sm:w-14',
              step === 2 ? 'bg-accent-border' : 'bg-primary-border',
            )}
          />
          <span
            className={cn(
              'inline-flex size-8 items-center justify-center rounded-full border text-caption font-bold',
              step === 2
                ? 'border-primary bg-primary text-text-inverse'
                : 'border-border-strong bg-surface text-text-secondary',
            )}
          >
            2
          </span>
        </div>
        <p className="text-label text-text-secondary" aria-live="polite">
          {t('onboarding.step', [step])}
        </p>
      </div>
      <h2
        id="onboarding-heading"
        ref={heading}
        tabIndex={-1}
        className="text-h3 font-bold text-text focus:outline-none"
      >
        {t(state.role ? 'onboarding.profileTitle' : 'onboarding.subtitle')}
      </h2>
      {state.role ? (
        <form
          noValidate
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            const input = {
              role: state.role!,
              firstName,
              lastName,
              ...(state.role === 'STUDENT' ? { birthDate } : {}),
            }
            const parsed = completeOnboardingInput.safeParse(input)
            if (!parsed.success) {
              const next: Record<string, string> = {}
              for (const issue of parsed.error.issues) {
                const field = String(issue.path[0])
                next[field] = t(
                  field === 'birthDate' ? 'onboarding.dateError' : 'onboarding.nameError',
                )
              }
              setErrors(next)
              const first = Object.keys(next)[0]
              if (first) document.getElementById(`onboarding-${first}`)?.focus()
              return
            }
            setErrors({})
            void complete(input)
          }}
        >
          <TextInput
            id="onboarding-firstName"
            label={t('onboarding.firstName')}
            autoComplete="given-name"
            required
            maxLength={60}
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            {...(errors['firstName'] ? { error: errors['firstName'] } : {})}
          />
          <TextInput
            id="onboarding-lastName"
            label={t('onboarding.lastName')}
            autoComplete="family-name"
            required
            maxLength={60}
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            {...(errors['lastName'] ? { error: errors['lastName'] } : {})}
          />
          {state.role === 'STUDENT' ? (
            <TextInput
              id="onboarding-birthDate"
              label={t('onboarding.birthDate')}
              type="date"
              autoComplete="bday"
              required
              min="1900-01-01"
              max={new Date().toISOString().slice(0, 10)}
              value={birthDate}
              onChange={(event) => setBirthDate(event.target.value)}
              {...(errors['birthDate'] ? { error: errors['birthDate'] } : {})}
            />
          ) : null}
          <div className="flex flex-wrap justify-between gap-3">
            {state.canChangeRole ? (
              <Button
                type="button"
                variant="secondary"
                disabled={saving}
                onClick={() => void selectRole(null)}
              >
                {t('onboarding.back')}
              </Button>
            ) : null}
            <Button type="submit" loading={saving} loadingText={t('onboarding.saving')}>
              {t('onboarding.continue')}
            </Button>
          </div>
        </form>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <RoleChoice
            id="onboarding-role-tutor"
            title={t('onboarding.tutorTitle')}
            description={t('onboarding.tutorDescription')}
            icon={<Icon.Presentation className="size-icon-md" aria-hidden="true" />}
            disabled={saving}
            onClick={() => void selectRole('TUTOR')}
          />
          <RoleChoice
            id="onboarding-role-student"
            title={t('onboarding.studentTitle')}
            description={t('onboarding.studentDescription')}
            icon={<Icon.GraduationCap className="size-icon-md" aria-hidden="true" />}
            disabled={saving}
            onClick={() => void selectRole('STUDENT')}
          />
        </div>
      )}
      {error ? (
        <div role="alert">
          <FieldMessage tone="error">{error}</FieldMessage>
        </div>
      ) : null}
    </section>
  )
}

function CompletedSteps() {
  return (
    <div className="flex items-center" aria-hidden="true">
      <span className="inline-flex size-8 items-center justify-center rounded-full border border-accent-border bg-accent text-caption font-bold text-avatar-on-accent">
        1
      </span>
      <span className="h-px w-10 bg-accent-border sm:w-14" />
      <span className="inline-flex size-8 items-center justify-center rounded-full border border-accent-border bg-accent text-caption font-bold text-avatar-on-accent">
        2
      </span>
    </div>
  )
}

function RoleChoice({
  id,
  title,
  description,
  icon,
  disabled,
  onClick,
}: {
  id: string
  title: string
  description: string
  icon: ReactNode
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-description`}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'group flex min-h-40 cursor-pointer flex-col items-start gap-4 rounded-md border border-border bg-surface p-5 text-left',
        'transition-[background-color,border-color,box-shadow] duration-fast hover:border-primary hover:bg-primary-light hover:shadow-elevation-1',
        'active:border-primary-dark disabled:cursor-default disabled:border-border disabled:bg-surface-secondary disabled:shadow-none disabled:hover:border-border disabled:hover:bg-surface-secondary',
        FOCUS_RING,
      )}
    >
      <span className="inline-flex size-10 items-center justify-center rounded-sm bg-primary-light text-primary-dark group-hover:bg-surface">
        {icon}
      </span>
      <span className="flex flex-col gap-1">
        <span id={`${id}-title`} className="text-h4 font-semibold text-text">
          {title}
        </span>
        <span id={`${id}-description`} className="text-body-sm text-text-secondary">
          {description}
        </span>
      </span>
    </button>
  )
}
